import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export function BackupManager({ authToken }) {
  const [backupInfo, setBackupInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [message, setMessage] = useState(null);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${authToken}` } }),
    [authToken]
  );

  // Backup-Info laden
  useEffect(() => {
    const loadInfo = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE}/api/backup/info`, authHeaders());
        setBackupInfo(res.data);
      } catch (error) {
        console.error("Fehler beim Laden der Backup-Informationen:", error);
        setMessage({ type: "error", text: "Backup-Informationen konnten nicht geladen werden" });
      }
      setLoading(false);
    };
    loadInfo();
  }, [authHeaders]);

  // Backup erstellen und herunterladen
  const handleCreateBackup = async () => {
    setDownloading(true);
    setMessage(null);
    
    try {
      const response = await axios.get(`${API_BASE}/api/backup/create`, {
        ...authHeaders(),
        responseType: 'blob'
      });
      
      // Dateiname aus Header extrahieren oder generieren
      const contentDisposition = response.headers['content-disposition'];
      let filename = `schwimmkurs_backup_${new Date().toISOString().slice(0,10)}.zip`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      // Download auslösen
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setMessage({ type: "success", text: "Backup erfolgreich erstellt und heruntergeladen!" });
    } catch (error) {
      console.error("Fehler beim Erstellen des Backups:", error);
      setMessage({ type: "error", text: "Backup konnte nicht erstellt werden" });
    }
    
    setDownloading(false);
  };

  // Kollektion-Namen auf Deutsch übersetzen
  const translateCollectionName = (name) => {
    const translations = {
      "users": "Benutzer",
      "children": "Kinder",
      "groups": "Gruppen",
      "attendance": "Anwesenheit",
      "course_dates": "Kurstermine",
      "mail_settings": "E-Mail-Einstellungen",
      "mail_signature": "E-Mail-Signatur",
      "mail_templates": "E-Mail-Vorlagen",
      "permissions": "Berechtigungen",
      "roles": "Rollen",
      "training_sections": "Ausbildungsabschnitte",
      "faq_categories": "FAQ-Kategorien",
      "faq_entries": "FAQ-Einträge",
      "font_settings": "Schrifteinstellungen",
      "device_breakpoints": "Geräte-Einstellungen",
      "footer_settings": "Footer-Einstellungen",
      "session_settings": "Session-Einstellungen",
      "email_logs": "E-Mail-Protokoll",
      "import_history": "Import-Verlauf",
    };
    return translations[name] || name;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-violet-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="backup-manager">
      {/* Header */}
      <div className="rounded-xl bg-gradient-to-r from-emerald-50 to-white p-4 shadow-sm">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-bold text-slate-900" style={{ fontSize: 'var(--font-h3-size)', fontWeight: 'var(--font-h3-weight)' }}>
              Datenbank-Backup
            </h3>
            <p className="mt-1 text-slate-500" style={{ fontSize: 'var(--font-body-size)' }}>
              Erstellen Sie ein vollständiges Backup aller Daten als ZIP-Datei.
            </p>
          </div>
          <button
            onClick={handleCreateBackup}
            disabled={downloading}
            className="flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-white hover:bg-emerald-500 disabled:opacity-50 transition-all"
            style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-body-weight)' }}
          >
            {downloading ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                <span>Backup wird erstellt...</span>
              </>
            ) : (
              <>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Backup erstellen & herunterladen</span>
              </>
            )}
          </button>
        </div>

        {/* Status-Meldung */}
        {message && (
          <div
            className={`mt-3 rounded-lg p-3 ${
              message.type === "success"
                ? "bg-emerald-100 text-emerald-700"
                : "bg-rose-100 text-rose-700"
            }`}
            style={{ fontSize: 'var(--font-body-size)' }}
          >
            {message.text}
          </div>
        )}
      </div>

      {/* Datenbank-Übersicht */}
      {backupInfo && (
        <div className="rounded-xl bg-white p-4 shadow-sm">
          <h4 className="mb-4 text-slate-900" style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-label-weight)' }}>
            Datenbank-Übersicht
          </h4>
          
          {/* Statistik */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
            <div className="rounded-lg bg-slate-50 p-3 text-center">
              <div className="text-2xl font-bold text-emerald-600">{backupInfo.total_documents}</div>
              <div className="text-slate-500" style={{ fontSize: 'var(--font-small-size)' }}>Datensätze gesamt</div>
            </div>
            <div className="rounded-lg bg-slate-50 p-3 text-center">
              <div className="text-2xl font-bold text-blue-600">{backupInfo.collections?.length || 0}</div>
              <div className="text-slate-500" style={{ fontSize: 'var(--font-small-size)' }}>Kollektionen</div>
            </div>
            <div className="rounded-lg bg-slate-50 p-3 text-center sm:col-span-1 col-span-2">
              <div className="text-lg font-bold text-slate-600">{backupInfo.db_name}</div>
              <div className="text-slate-500" style={{ fontSize: 'var(--font-small-size)' }}>Datenbank</div>
            </div>
          </div>

          {/* Kollektionen-Liste */}
          <div className="border-t border-slate-100 pt-4">
            <h5 className="mb-2 text-slate-700" style={{ fontSize: 'var(--font-small-size)', fontWeight: 'var(--font-label-weight)' }}>
              Enthaltene Daten:
            </h5>
            <div className="flex flex-wrap gap-2">
              {backupInfo.collections?.map((col) => (
                <div 
                  key={col.name} 
                  className="flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5"
                >
                  <span className="text-slate-700" style={{ fontSize: 'var(--font-small-size)' }}>
                    {translateCollectionName(col.name)}
                  </span>
                  <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">
                    {col.count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Was wird gesichert */}
      <div className="rounded-xl bg-white p-4 shadow-sm">
        <h4 className="mb-3 text-slate-900" style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-label-weight)' }}>
          Was wird gesichert?
        </h4>
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="flex items-center gap-2 text-slate-600" style={{ fontSize: 'var(--font-body-size)' }}>
            <svg className="h-5 w-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Alle Kinder und deren Daten</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600" style={{ fontSize: 'var(--font-body-size)' }}>
            <svg className="h-5 w-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Gruppen und Kurstermine</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600" style={{ fontSize: 'var(--font-body-size)' }}>
            <svg className="h-5 w-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Anwesenheitsdaten</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600" style={{ fontSize: 'var(--font-body-size)' }}>
            <svg className="h-5 w-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Benutzer und Berechtigungen</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600" style={{ fontSize: 'var(--font-body-size)' }}>
            <svg className="h-5 w-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Ausbildungsstand-Konfiguration</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600" style={{ fontSize: 'var(--font-body-size)' }}>
            <svg className="h-5 w-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Alle Systemeinstellungen</span>
          </div>
        </div>
      </div>

      {/* Hinweis */}
      <div className="rounded-lg bg-blue-50 p-3 text-blue-700" style={{ fontSize: 'var(--font-body-size)' }}>
        <strong>Hinweis:</strong> Das Backup enthält alle Daten im JSON-Format, verpackt als ZIP-Datei. 
        Bewahren Sie das Backup an einem sicheren Ort auf.
      </div>

      {/* Sicherheitshinweis */}
      <div className="rounded-lg bg-amber-50 p-3 text-amber-700" style={{ fontSize: 'var(--font-body-size)' }}>
        <strong>Wichtig:</strong> Das Backup enthält sensible Daten (z.B. Kinderinformationen, E-Mail-Adressen). 
        Behandeln Sie die Datei vertraulich und gemäß den Datenschutzbestimmungen.
      </div>
    </div>
  );
}
